import { useContext, useEffect, useReducer, useMemo } from "react";
import { AuthContext } from "../context/Auth/AuthContext";
import socket from "./useSocket";
import api from "../services/api";

const ticketsSharedsReducer = (state, action) => {
	const { type, payload, status: statusTab } = action;

	switch (type) {
		case "LOAD_TICKETS": {
			const newTickets = payload;
			const ticketMap = new Map(state.map(ticket => [ticket.id, ticket]));

			// Atualizar tickets existentes e adicionar novos
			newTickets.forEach(ticket => {
				ticketMap.set(ticket.id, ticket);
			});

			const result = Array.from(ticketMap.values());

			// Ordenação simples: apenas por unread messages
			const unreadTickets = result.filter(t => t.unreadMessages > 0);
			const readTickets = result.filter(t => t.unreadMessages === 0);

			return [...unreadTickets, ...readTickets];
		}

		case "RESET_UNREAD": {
			const ticketIndex = state.findIndex(t => t.id === payload);

			if (ticketIndex === -1) return state;

			const currentTicket = state[ticketIndex];
			if (currentTicket.unreadMessages === 0) return state;

			const newState = [...state];
			newState[ticketIndex] = { ...currentTicket, unreadMessages: 0 };

			// Reordenar apenas se o ticket tinha mensagens não lidas
			const unreadTickets = newState.filter(t => t.unreadMessages > 0);
			const readTickets = newState.filter(t => t.unreadMessages === 0);

			return [...unreadTickets, ...readTickets];
		}

		case "UPDATE_TICKET": {
			const ticket = payload;

			const ticketIndex = state.findIndex(t => t.id === ticket.id);
			if (ticketIndex !== -1) {
				// Se o ticket mudou de status e não pertence mais a esta aba, remover
				if (ticket.status !== statusTab) {
					return state.filter(t => t.id !== ticket.id);
				}

				const currentTicket = state[ticketIndex];
				const newState = [...state];
				newState[ticketIndex] = ticket;

				// Só reordenar se o status de unread mudou
				if (currentTicket.unreadMessages !== ticket.unreadMessages) {
					const unreadTickets = newState.filter(t => t.unreadMessages > 0);
					const readTickets = newState.filter(t => t.unreadMessages === 0);
					return [...unreadTickets, ...readTickets];
				}

				return newState;
			} else {
				// Ticket novo - apenas adicionar se pertence a esta aba
				if (ticket.status !== statusTab) return state;

				if (ticket.unreadMessages > 0) {
					// Adicionar no início (com outros unread)
					const unreadTickets = [ticket, ...state.filter(t => t.unreadMessages > 0)];
					const readTickets = state.filter(t => t.unreadMessages === 0);
					return [...unreadTickets, ...readTickets];
				} else {
					// Adicionar no final (com outros read)
					const unreadTickets = state.filter(t => t.unreadMessages > 0);
					const readTickets = [...state.filter(t => t.unreadMessages === 0), ticket];
					return [...unreadTickets, ...readTickets];
				}
			}
		}

		case "UPDATE_TICKET_UNREAD_MESSAGES": {
			const { ticketId, unreadMessages } = payload;
			const ticketIndex = state.findIndex(t => t.id === ticketId);

			if (ticketIndex === -1) return state;

			const currentTicket = state[ticketIndex];
			if (currentTicket.unreadMessages === unreadMessages) return state;

			const newState = [...state];
			newState[ticketIndex] = { ...currentTicket, unreadMessages };

			// Só reordenar se mudou de read para unread ou vice-versa
			const wasUnread = currentTicket.unreadMessages > 0;
			const isNowUnread = unreadMessages > 0;

			if (wasUnread !== isNowUnread) {
				const unreadTickets = newState.filter(t => t.unreadMessages > 0);
				const readTickets = newState.filter(t => t.unreadMessages === 0);
				return [...unreadTickets, ...readTickets];
			}

			return newState;
		}

		case "DELETE_TICKET": {
			return state.filter(ticket => ticket.id !== payload);
		}

		case "RESET": {
			return [];
		}

		default: {
			return state;
		}
	}
};

const useSharedTickets = (status) => {
	const [ticketsSharedList, dispatch] = useReducer(ticketsSharedsReducer, []);
	const { user } = useContext(AuthContext);
	const userCompany = user?.companyId;

	// Buscar tickets compartilhados
	useEffect(() => {
		if (status !== "open") return;

		const fetchSharedTickets = async () => {
			try {
				const { data } = await api.get(`/tickets/shared`, {
					params: { status }
				});
				dispatch({ type: "LOAD_TICKETS", payload: data.tickets });
			} catch (err) {
				console.error("Erro ao buscar tickets compartilhados:", err);
			}
		};

		fetchSharedTickets();
	}, [status]);

	// Socket listeners para tickets compartilhados
	useEffect(() => {
		if (!userCompany) return;

		const handleSharedTicketUpdate = (data) => {
			console.info("Shared ticket update:", data);

			switch (data.action) {
				case "update":
					if (Array.isArray(data?.users) && data?.users?.some(u => u.id === user.id)) {
						dispatch({
							type: "UPDATE_TICKET",
							payload: data.ticket,
							status
						});
					}
					break;
				case "delete":
					dispatch({
						type: "DELETE_TICKET",
						payload: data.ticketId
					});
					break;
				case "updateUnread":
					dispatch({
						type: "UPDATE_TICKET_UNREAD_MESSAGES",
						payload: {
							ticketId: data.ticketId,
							unreadMessages: data.unreadMessages
						}
					});
					break;
				case "resetUnread":
					dispatch({
						type: "RESET_UNREAD",
						payload: data.ticketId
					});
					break;
			}
		};

		// Handler nomeado para permitir o off() do segundo listener.
		const handleSharedTicketDelete = (data) => {
			dispatch({ type: "DELETE_TICKET", payload: data.ticketId });
		};

		socket.on(`ticket-${userCompany}-shared`, handleSharedTicketUpdate);
		socket.on(`ticket-${userCompany}-shared-${user.id}-delete`, handleSharedTicketDelete);

		return () => {
			socket.off(`ticket-${userCompany}-shared`, handleSharedTicketUpdate);
			socket.off(`ticket-${userCompany}-shared-${user.id}-delete`, handleSharedTicketDelete);
		};
	}, [userCompany, status, user.id]);

	// Memoizar se deve mostrar a seção
	const shouldShowSharedTickets = useMemo(() => {
		return status === "open" && ticketsSharedList.length > 0;
	}, [status, ticketsSharedList.length]);

	return {
		ticketsSharedList,
		shouldShowSharedTickets,
		dispatch
	};
};

export default useSharedTickets;